import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import {
  AddBulkUsers,
  AddBulkUsersResponse,
  NewUserInputs,
  UpdateUserInputs,
  UserDisplay,
} from "@/types/admin";
import {
  ApiResponse,
  LoginWithAccessToken,
  UpdateUserCommunicationInfo,
  UpdateUserProfileInputs,
  UserCommunicationInfo,
  UserCommunicationInfoDisplay,
  UserInfo,
  UserProfileDisplay,
} from "@/types/global";
import type { LinkGoogleAccountInputs } from "@/types/userManagement";

export default class UserApi extends ApiVerified {
  // user.json LoginWithAccessToken — body may carry an optional `deviceTypeId` (default 1) (AR-20.1).
  async loginWithThirdPartyAccessToken(
    loginCredentials: LoginWithAccessToken,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<UserInfo>(
        `user/login-with-third-party-access-token`,
        loginCredentials,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async changePassword(
    userId: number,
    currentPassword: string,
    newPassword: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`user/change-password`, {
        UserId: userId,
        CurrentPassword: currentPassword,
        NewPassword: newPassword,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // User Management v3 (pvni-user-mgmt-v3) — link a Google account (SP4, api-binding §4).
  // POST /v7/user/link-google-account → true. 401 JWT · 409 "This Google account is already linked
  // to another user." (BR-UI-301/302 — do not suggest unlink). Rate-limited 5/s, 10/min.
  async linkGoogleAccount(inputs: LinkGoogleAccountInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`user/link-google-account`, inputs);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // User Management v3 (pvni-user-mgmt-v3) — unlink the Google account (SP4, api-binding §5).
  // POST /v7/user/unlink-google-account → true (no body; user resolved from JWT). 404 "No linked
  // Google account found for this user." Soft-deactivation; re-link reactivates. UI blocks unlink for
  // SSO-only accounts (hasPassword === false) + confirms first — BR-UI-307 (enforced at 7-5 call site).
  async unlinkGoogleAccount(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`user/unlink-google-account`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async resetUserPassword(
    userId: number,
    newPassword: string,
    setToDefault: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`user/reset-user-password`, {
        UserId: userId,
        NewPassword: newPassword,
        SetToDefault: setToDefault,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async resetPasswordForUsers(userIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`user/bulk-reset-user-passwords`, {
        UserIds: userIds,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentProfile(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<UserProfileDisplay>(`user/get-user-profile`, {
        params: { userId: userId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateStudentProfile(studentProfileData: UpdateUserProfileInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `user/update-user-profile`,
        studentProfileData,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getUserCommunicationInfo(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<UserCommunicationInfoDisplay>(
        `user/get-user-communication-info`,
        { params: { userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async sendCommunicationDetailOtp(
    communicationInfo: UpdateUserCommunicationInfo,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(
        `user/send-communication-detail-otp`,
        communicationInfo,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateCommunicationInfo(
    userCommunicationInfo: UserCommunicationInfo,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `user/update-communication-info`,
        userCommunicationInfo,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateSessionLastAccessed(loginSessionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`user/update-session-last-accessed`, {
        LoginSessionId: loginSessionId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async registerNewUser(userData: NewUserInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`user/register-B2B-user`, userData);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async registerBulkUsers(
    affiliationId: number,
    userData: AddBulkUsers,
    csvFile: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AddBulkUsersResponse>(
        `user/register-bulk-users`,
        { AffiliationId: affiliationId, UserData: userData, CsvFile: csvFile },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateUser(userId: number, updateData: UpdateUserInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`user/update-user`, {
        UserId: userId,
        UpdateData: updateData,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateUserStatus(userId: number, isActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`user/update-user-status`, {
        UserId: userId,
        IsActive: isActive,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateBulkUsersBatch(
    affiliationId: number,
    sourceBatchId: number,
    targetBatchId: number,
    userIds: Array<number>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`user/update-bulk-users-batch`, {
        AffiliationId: affiliationId,
        SourceBatchId: sourceBatchId,
        TargetBatchId: targetBatchId,
        UserIds: userIds,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getUsersBySearchText(searchText: string) {
    try {
      const response = await this.axiosInstance.get<Array<UserDisplay>>(
        `user/users-by-search-text`,
        { params: { searchText: searchText } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async submitDeleteAccountPermanently(
    userId: number,
    loginId: string,
    password: string,
    appName: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`user/delete-account-permanently`, {
        UserId: userId,
        LoginId: loginId,
        Password: password,
        AppName: appName,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getUsersByRoleId(roleId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<import("@/types/global").IdNamePair>>(
        `user/get-users-by-role-id`,
        { params: { roleId: roleId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
